import { DisplayTypeEnum } from "api/files";
import { ConfigGarantieItemDto, ParcConfigDto, PropertyTypeEnum } from "api/real-estate";
import { GenericColors } from "api/tenant";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { toasterService } from "components/feedback/Toaster/toasterService";
import { NsKey } from "enums/NsKey";
import { formatAddress, formatPropertyType } from "features/subscription-process/home-insurance/utils/realEstateFormatters";
import { useDialog } from "hooks/useDialog";
import { ChevronRight, RotateCcw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";

import GuaranteeFilter from "../guarantees-card/GuaranteeFilter";
import { useParcBienTypes } from "../../hooks/useParcBienTypes";
import { useParcBiensInfinite } from "../../hooks/useParcBiensInfinite";
import { clearGuaranteeLastResearchHash, updateAllContractFunnelsItem } from "../../slices/pricing/pricingSlice";
import { selectGlobalGuarantees, selectGlobalGuaranteesReferential } from "../../slices/pricing/selectors/guaranteeFilterSelector";
import { fetchFunnels } from "../../slices/pricing/thunks/fetchFunnelsThunk";
import { refetchGlobalContracts } from "../../slices/pricing/thunks/refetchGlobalContractsThunk";
import { selectAllValuesForAPI, selectFees, selectVariables } from "../../slices/pricing-values/selectors";
import { updateGlobalFeeValue, updateGlobalVariableValue } from "../../slices/pricing-values/pricingValuesSlice";
import { buildSelectionFromConfigItems } from "../../slices/pricing/utils/parcGuaranteesUtils";
import { GuaranteesReferential } from "../../types/interfaces";

interface ParcConfigurationTabProps {
  riskId: string;
  config?: ParcConfigDto;
  /** Rafraîchit la config (après enregistrement). */
  onSaved?: () => void;
}

/**
 * Configuration des biens : couverture GLOBALE (éditée via la carte garanties) + override PAR BIEN.
 * Liste des biens en infinite scroll (recherche + filtre type côté serveur — tient à 200+ biens) ;
 * le « Personnaliser » bascule la vue (slide interne) vers le composant de garanties réutilisé.
 */
export const ParcConfigurationTab: React.FC<ParcConfigurationTabProps> = ({ riskId, config, onSaved }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(NsKey.RealEstate);
  const { globalGuaranteesReferential } = useAppSelector(selectGlobalGuaranteesReferential);
  const { mandatory, optional } = globalGuaranteesReferential;
  const globalGuarantees = useAppSelector(selectGlobalGuarantees);
  const proModeValues = useAppSelector(selectAllValuesForAPI);
  const feesConfig = useAppSelector(selectFees);
  const variablesConfig = useAppSelector(selectVariables);

  // Recherche (debouncée) + filtre type, pilotés côté serveur.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<number | null>(null);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const { biens, isLoading, isLoadingMore, hasMore, loadMore } = useParcBiensInfinite(riskId, {
    search: debouncedSearch,
    type: typeFilter,
  });
  const availableTypes = useParcBienTypes(riskId);

  // Conversion config (niveau garantie-produit) -> sélection par bien (cf. util partagé avec le hydrator).
  const initialOverrides = useMemo(() => {
    const result: Record<string, GuaranteesReferential> = {};
    for (const [bienId, items] of Object.entries(config?.overrides ?? {})) {
      result[bienId] = buildSelectionFromConfigItems(items, globalGuaranteesReferential);
    }
    return result;
  }, [config, globalGuaranteesReferential]);

  const [overrides, setOverrides] = useState<Record<string, GuaranteesReferential>>(initialOverrides);
  const [editingBien, setEditingBien] = useState<{ id: string; name: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Titre dynamique de la modale selon la vue (liste vs configuration d'un bien).
  const { setDialogTitle, setDialogActions } = useDialog();
  useEffect(() => {
    setDialogTitle(editingBien ? `Couverture de ${editingBien.name}` : "Configuration du parc immobilier");
  }, [editingBien, setDialogTitle]);

  const personnaliser = (bien: { id: string; name: string }) => {
    setEditingBien(bien);
  };

  const reinitialiser = (bienId: string) =>
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[bienId];
      return next;
    });

  // Sélection (GuaranteeDto + franchise/plafond) -> items config (niveau garantie-produit).
  const buildItems = (sel: GuaranteesReferential): ConfigGarantieItemDto[] => {
    const optById = new Map(sel.optional.map((g) => [g.id, g]));
    const mandById = new Map(sel.mandatory.map((g) => [g.id, g]));
    return [
      ...mandatory.map((g) => {
        const s = mandById.get(g.id);
        return { garantieId: g.id, active: true, franchise: s?.deductible?.value, plafond: s?.ceiling?.value };
      }),
      ...optional.map((g) => {
        const s = optById.get(g.id);
        return { garantieId: g.id, active: !!s, franchise: s?.deductible?.value, plafond: s?.ceiling?.value };
      }),
    ];
  };

  const onSave = useCallback(async () => {
    if (!config) return;
    setIsSaving(true);
    try {
      const global: ConfigGarantieItemDto[] = [
        ...globalGuarantees.mandatory,
        ...globalGuarantees.optional,
      ].map((g) => ({
        garantieId: g.id,
        active: true,
        franchise: g.deductible?.value,
        plafond: g.ceiling?.value,
      }));

      const overridesConfig: Record<string, ConfigGarantieItemDto[]> = {};
      for (const [bienId, sel] of Object.entries(overrides)) {
        overridesConfig[bienId] = buildItems(sel);
      }

      const response = await realEstateAPI.saveParcConfig({
        riskId,
        parcConfigDto: { ...config, global, overrides: overridesConfig },
      });
      if (response.isSuccess) {
        toasterService.showToast({
          message: "Couverture enregistrée — tarifs mis à jour.",
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Green,
        });
        onSaved?.();

        // Le fetchFunnels complet écrase les frais/variables du mode pro avec les défauts
        // du tunnel (pricingValuesSlice, reducer fetchFunnels.fulfilled) : on capture la
        // saisie avant, et si elle diffère des défauts on la réinjecte puis on rejoue la
        // tarification — même mécanique que refetchGlobalContracts pour le bien simple.
        const defaultFees = new Map(feesConfig.map((f) => [f.code, f.value]));
        const defaultVariables = new Map(variablesConfig.map((v) => [v.variableRefId, v.value]));
        const customFees = proModeValues.fees.filter((f) => defaultFees.get(f.code) !== f.value);
        const customVariables = proModeValues.variables.filter(
          (v) => defaultVariables.get(v.idReferentialVariable) !== v.value
        );

        dispatch(clearGuaranteeLastResearchHash());
        await dispatch(fetchFunnels(riskId));

        if (customFees.length > 0 || customVariables.length > 0) {
          customFees.forEach((f) => {
            dispatch(updateGlobalFeeValue({ itemId: f.code, value: f.value }));
            dispatch(updateAllContractFunnelsItem({ itemId: f.code, type: "fee", newValue: f.value }));
          });
          customVariables.forEach((v) => {
            dispatch(updateGlobalVariableValue({ id: v.idReferentialVariable, value: v.value }));
            dispatch(
              updateAllContractFunnelsItem({ itemId: v.idReferentialVariable, type: "variable", newValue: v.value })
            );
          });
          dispatch(refetchGlobalContracts());
        }
      } else {
        toasterService.showToast({
          message: response.errorMessage ?? "Erreur lors de l'enregistrement.",
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
      }
    } catch (error) {
      console.error("Erreur enregistrement config parc:", error);
      toasterService.showToast({
        message: "Erreur lors de l'enregistrement de la couverture.",
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setIsSaving(false);
    }
  }, [config, globalGuarantees, overrides, riskId, dispatch, onSaved, proModeValues, feesConfig, variablesConfig]);

  // Footer du dialog en vue liste : « Enregistrer la couverture ». En vue éditeur, c'est GuaranteeFilter qui pilote.
  useEffect(() => {
    if (editingBien) return;
    setDialogActions({
      submitLabel: isSaving ? "Enregistrement…" : "Enregistrer la couverture",
      submitDisabled: isSaving || !config,
      onSubmit: onSave,
    });
  }, [editingBien, isSaving, config, onSave, setDialogActions]);

  // Handlers stables (cf. recâblage du footer par GuaranteeFilter — évite la boucle de rendu).
  const handleApplyBien = useCallback(
    (sel: GuaranteesReferential) => {
      if (editingBien) setOverrides((prev) => ({ ...prev, [editingBien.id]: sel }));
    },
    [editingBien]
  );
  const handleBackToList = useCallback(() => setEditingBien(null), []);

  // Infinite scroll : sentinelle observée dans le conteneur scrollable.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = scrollRef.current;
    const el = sentinelRef.current;
    if (!root || !el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, rootMargin: "120px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  // Vue éditeur de garanties d'un bien (slide interne — pas de second overlay).
  if (editingBien) {
    return (
      <GuaranteeFilter
        key={editingBien.id}
        isOptional
        // Bien déjà personnalisé : son snapshot ; sinon le global ACTUEL comme point
        // de départ (l'override n'existera qu'après « Appliquer »).
        initialGuarantees={overrides[editingBien.id] ?? globalGuarantees}
        onApply={handleApplyBien}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-500">
        Chaque bien suit la couverture globale, sauf si vous le personnalisez.
      </p>

      {/* Barre d'outils : recherche + filtres type */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un bien (nom, adresse)"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        {availableTypes.length > 1 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setTypeFilter(null)}
              className={`text-xs px-3 py-1 rounded-full border ${typeFilter === null ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"}`}
            >
              Tous
            </button>
            {availableTypes.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setTypeFilter(type)}
                className={`text-xs px-3 py-1 rounded-full border ${typeFilter === type ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-gray-200 hover:bg-gray-50"}`}
              >
                {formatPropertyType(type as PropertyTypeEnum, t)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Liste scrollable + infinite scroll */}
      <div ref={scrollRef} className="max-h-[55vh] overflow-y-auto border-y border-gray-100 divide-y divide-gray-100">
        {biens.map((bien) => {
          const isCustom = bien.id in overrides;
          return (
            <div
              key={bien.id}
              role="button"
              tabIndex={0}
              onClick={() => personnaliser({ id: bien.id, name: bien.name })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  personnaliser({ id: bien.id, name: bien.name });
                }
              }}
              className="w-full flex items-center gap-4 px-3 py-4 text-left cursor-pointer hover:bg-gray-50"
            >
              <img
                src={bien.photoUrl || "/assets/images/real_estate_default.png"}
                alt={bien.name}
                className="w-10 h-10 rounded-lg object-cover flex-none"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{bien.name}</p>
                <p className="text-xs text-slate-500 truncate">{formatAddress(bien.address)}</p>
              </div>
              <div className="flex items-center gap-2 flex-none">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${
                    isCustom ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}
                >
                  {isCustom ? "Personnalisée" : "Globale"}
                </span>
                {isCustom && (
                  <button
                    type="button"
                    title="Réinitialiser (suivre le global)"
                    onClick={(e) => { e.stopPropagation(); reinitialiser(bien.id); }}
                    className="p-1 text-slate-400 hover:text-slate-600"
                  >
                    <RotateCcw size={14} />
                  </button>
                )}
                <ChevronRight size={16} className="text-slate-300" />
              </div>
            </div>
          );
        })}

        {isLoading && <div className="px-3 py-4 text-sm text-slate-400">Chargement des biens…</div>}
        {!isLoading && biens.length === 0 && (
          <div className="px-3 py-6 text-sm text-slate-400 text-center">Aucun bien ne correspond.</div>
        )}

        {/* Sentinelle infinite scroll */}
        <div ref={sentinelRef} />
        {isLoadingMore && <div className="px-3 py-3 text-xs text-slate-400 text-center">Chargement…</div>}
        {!hasMore && !isLoading && biens.length > 0 && (
          <div className="px-3 py-3 text-xs text-slate-300 text-center">Fin de la liste</div>
        )}
      </div>
    </div>
  );
};
